import { gql } from 'graphql-request';

/**
 * POM-6099 — relay orders (Diamond via Quello), and since POM-6279 also relay orders delivered by
 * fax (`deliveryMethod: FAX`). POM sys-admin only; core gates both queries on
 * `isPOMAdmin`, which is SYS_ADMIN alone.
 *
 * NOTE what these selections deliberately do NOT ask for. Core exposes no prescription payload,
 * no submitted request body, no vendor response and no webhook body for a relay order — those are
 * PHI that answer an engineering question rather than an operational one. There is nothing to
 * select, and nothing here should grow toward it: a portal change that needs a new PHI field is
 * an API change and belongs back in core with its own review.
 */
export const relayOrdersGql = gql`
  query relayOrders(
    $filter: RelayOrdersFilter
    $limit: Int
    $offset: Int
    $sortBy: RelayOrdersSortBy
  ) {
    relayOrders(filter: $filter, limit: $limit, offset: $offset, sortBy: $sortBy) {
      # EXACTLY what the table renders, and nothing else. patientId in particular is deliberately
      # absent: it is an MPI, it would sit in the same cached row as drugName and ndc -- a
      # drug-to-patient linkage for a whole page of patients -- and no column reads it. It is
      # selected by the DETAIL query, where one order is on screen because somebody asked for it.
      items {
        relayOrderId
        externalOrderId
        status
        # PHI, added 2026-09-22 on the PO's instruction. patientId is STILL not selected -- the
        # name is fetched INSTEAD of the MPI, never as well as, so the browser never holds the
        # identifier that correlates this patient across systems.
        patientName
        partnerName
        drugName
        ndc
        quantity
        lineItemCount
        transferredAt
        # POM-6279 P5: fax relays share this queue with API (Quello) relays. FAX or API, and the
        # pharmacy the order went to (id is the fallback when core has no name -- a pharmacy uuid,
        # not PHI). The relay's own reference (Quello order id / TX-) is in the DETAIL query only.
        deliveryMethod
        fulfillingPharmacyName
        fulfillingPharmacyId
      }
      totalCount
      limit
      offset
    }
  }
`;

/**
 * The detail read, fetched only when an operator opens one order.
 *
 * `shipToAddressText` and `eventData` are the two PHI-bearing values in this document, and they
 * are why this query is NOT part of the list selection: fetching them per row would put every
 * patient's address on the page into the browser's store before anyone asked to see one.
 */
/** POM-6279. Short-lived download links for a FAX relay's PDFs; core audits the request. */
export const relayFaxDocumentLinksGql = gql`
  query relayFaxDocumentLinks($relayOrderId: ID!) {
    relayFaxDocumentLinks(relayOrderId: $relayOrderId) {
      documentNumber
      fileName
      downloadUrl
    }
  }
`;

export const relayOrderDetailsGql = gql`
  query relayOrderDetails($relayOrderId: ID!) {
    relayOrderDetails(relayOrderId: $relayOrderId) {
      order {
        relayOrderId
        externalOrderId
        intakeRequestId
        submitRequestId
        partnerId
        patientId
        integrationId
        status
        lastAttemptAt
        vendorOrderId
        vendorScriptNumber
        shipToAddressId
        shipToAddressText
        transferredAt
        cancelledAt
        trackingNumber
        trackingUrl
        carrier
        createdTimestamp
        # POM-6279 P5. The method and destination for both kinds of relay; the last three are null
        # on every API relay. transmissionReference is RingCentral's message id: copyable text,
        # never a link.
        deliveryMethod
        fulfillingPharmacyName
        fulfillingPharmacyId
        relayRxNumber
        destinationEndpoint
        transmissionReference
        sendClaimedAt
      }
      lineItems {
        relayLineItemId
        ordinal
        prescriptionUuid
        productId
        catalogId
        drugName
        ndc
        quantity
        vendorOrderId
        vendorScriptNumber
      }
      events {
        eventId
        eventType
        actor
        pomUserId
        requestId
        vendorReference
        httpStatus
        eventData
        createdTimestamp
      }
      hasMoreEvents
    }
  }
`;
